import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useLocation } from "react-router-dom";
import Table from "../../../core/common/dataTable";
import { showToast } from "../../../core/utils/alert";
import { StudentUIType } from "./types/promotionResponse";
import { usePromotion } from "./hooks/usePromotion";
import { usePromotionFilters } from "./hooks/usePromotionFilters";
import { useStudent } from "../../peoples/students/hooks/useStudent";
import { useCourse } from "../../configuration/course/hooks/useCourse";
import { useSession } from "../../configuration/session/hooks/useSession";
import PromotionModals from "./promotionModals";

const StudentPromotion = () => {
  const routes = all_routes;
  const location = useLocation();

  // State for edit mode
  const [isEdit, setIsEdit] = useState<boolean>(false);

  // State for promoting individual student
  const [promotingStudentId, setPromotingStudentId] = useState<number | null>(
    null
  );

  // State for selected students
  const [selectedStudentKeys, setSelectedStudentKeys] = useState<React.Key[]>(
    []
  );

  // State for student to promote
  const [studentToPromote, setStudentToPromote] = useState<
    StudentUIType | undefined
  >(undefined);

  // State for modals
  const [showPromoteModal, setShowPromoteModal] = useState<boolean>(false);
  const [showBulkPromoteModal, setShowBulkPromoteModal] =
    useState<boolean>(false);

  // Use the promotion filters hook
  const {
    selectedSession,
    selectedCourse,
    selectedFromStage,
    selectedToStage,
    recordsPerPage,
    pageNumber,
    search,
    courseStages,
    handleSessionChange,
    handleCourseChange,
    handleFromStageChange,
    handleToStageChange,
    handleSearchChange,
    handlePageChange,
    getStageOptions,
    getToStageOptions,
    areAllFiltersSelected,
    areAllFiltersWithToStageSelected,
    getMissingFiltersMessage,
    isLastStage,
  } = usePromotionFilters();

  // Get data from hooks
  const { getCourseState } = useCourse({ published: true });
  const { getSessionState } = useSession();
  const { courses, isLoading: isLoadingCourses } = getCourseState();
  const { sessions, isLoading: isLoadingSessions } = getSessionState();

  // Get student data
  const { getStudentState, paginationData } = useStudent({
    page: pageNumber,
    size: recordsPerPage,
    search: search,
    course_id: selectedCourse ? parseInt(selectedCourse) : undefined,
    stage_id: selectedFromStage ? parseInt(selectedFromStage) : undefined,
    session_id: selectedSession ? parseInt(selectedSession) : undefined,
    marks: true,
  });

  const { students, isLoading: isLoadingStudents, error } = getStudentState();

  // Use the promotion hook
  const {
    getPromotionState,
    promoteStudent,
    promoteStudentsBulk,
    isPromoting,
    isPromotingBulk,
  } = usePromotion({
    session_id: selectedSession,
    course_id: selectedCourse,
    from_stage_id: selectedFromStage,
  });

  // Set edit mode based on location
  useEffect(() => {
    // Check if we're in edit mode based on path
    setIsEdit(location.pathname.includes("edit"));
  }, [location.pathname]);

  // Update course stages when course changes
  useEffect(() => {
    if (selectedCourse) {
      handleCourseChange(
        {
          target: { value: selectedCourse },
        } as React.ChangeEvent<HTMLSelectElement>,
        courses
      );
    }
  }, [courses]);

  // Transform student data for the table
  const transformedStudents: StudentUIType[] = useMemo(() => {
    return students.map((student) => {
      // Find existing mark for this student in the current stage/course
      const existingMark = student.marks?.find(
        (mark) =>
          mark.stage?.id === Number(selectedFromStage) &&
          mark.course?.id === Number(selectedCourse)
      );

      // Get marks data if available
      let marksData = undefined;
      if (
        existingMark &&
        existingMark.stage?.obtained !== undefined &&
        existingMark.stage?.max_marks !== undefined &&
        existingMark.stage?.min_marks !== undefined
      ) {
        marksData = {
          obtained: existingMark.stage.obtained,
          max: existingMark.stage.max_marks,
          min: existingMark.stage.min_marks,
        };
      }

      // Calculate result based on marks
      const result = marksData
        ? marksData.obtained >= marksData.min
          ? "Pass"
          : "Fail"
        : "Not Marked";

      return {
        id: student.id,
        studentId: student.profile.studentid || "",
        name: `${student.first_name} ${student.last_name}`,
        photo: student.profile.photo || "",
        course: student.course?.name,
        courseName: student.course?.name,
        currentStage: student.course?.stage?.name,
        stageName: student.course?.stage?.name,
        stage_id: student.course?.stage?.id,
        course_id: student.course?.id,
        result: result,
        status: student.status,
        marks: marksData,
      };
    });
  }, [students, selectedFromStage, selectedCourse]);

  const handleSelectionChange = (selectedKeys: React.Key[]) => {
    setSelectedStudentKeys(selectedKeys);
  };

  const handlePromoteIndividual = (student: StudentUIType) => {
    if (
      !selectedCourse ||
      !selectedFromStage ||
      !selectedToStage ||
      !selectedSession
    ) {
      showToast({
        title: "Error",
        text: "Please select course, stages and session for promotion",
        icon: "error",
        timer: 5000,
      });
      return;
    }

    // Set the student to promote and show the confirmation modal
    setStudentToPromote(student);
    setShowPromoteModal(true);
  };

  const confirmIndividualPromotion = async () => {
    if (!studentToPromote) return;

    setPromotingStudentId(studentToPromote.id);

    try {
      await promoteStudent(studentToPromote.id);
      setShowPromoteModal(false);
    } finally {
      setPromotingStudentId(null);
    }
  };

  const handlePromoteAll = () => {
    if (
      !selectedCourse ||
      !selectedFromStage ||
      !selectedToStage ||
      !selectedSession ||
      selectedStudentKeys.length === 0
    ) {
      showToast({
        title: "Error",
        text: "Please select course, stages, session and students to promote",
        icon: "error",
        timer: 5000,
      });
      return;
    }

    // Show the bulk promotion confirmation modal
    setShowBulkPromoteModal(true);
  };

  const confirmBulkPromotion = async () => {
    // Convert the selected keys to numbers
    const studentIds = selectedStudentKeys.map((id) => Number(id));

    const result = await promoteStudentsBulk(studentIds);

    if (result.success) {
      // Reset selection after successful promotion
      setSelectedStudentKeys([]);
      setShowBulkPromoteModal(false);
    }
  };

  // Get checkbox props for each row
  const getCheckboxProps = (record: StudentUIType) => ({
    disabled: record.result === "Fail" || record.result === "Not Marked", // Disable selection for failed students
    name: record.name,
  });

  // Table columns
  const columns = [
    {
      title: "Student ID",
      dataIndex: "studentId",
      key: "studentId",
      render: (text: string, record: any) => {
        return (
          <Link to="#" className="link-primary">
            {text}
          </Link>
        );
      },
      sorter: (a: any, b: any) =>
        (a.studentId || "").localeCompare(b.studentId || ""),
    },
    {
      title: "Student Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: StudentUIType) => (
        <div className="d-flex align-items-center">
          <p className="text-dark mb-0">{text}</p>
        </div>
      ),
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
    },
    {
      title: "Course",
      key: "courseName",
      dataIndex: "courseName",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <p className="text-dark mb-0">{text}</p>
        </div>
      ),
    },
    {
      title: "Current Level",
      key: "stageName",
      dataIndex: "stageName",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <p className="text-dark mb-0">{text}</p>
        </div>
      ),
    },
    {
      title: "Result",
      key: "result",
      dataIndex: "result",
      render: (text: string) => (
        <span
          className={`badge badge-soft-${text === "Pass"
            ? "success"
            : text === "Not Marked"
              ? "warning"
              : "danger"
            } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {text}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: string, record: StudentUIType) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <button
                  className="dropdown-item rounded-1"
                  onClick={() => handlePromoteIndividual(record)}
                  disabled={
                    promotingStudentId === record.id ||
                    record.result === "Fail" ||
                    record.result === "Not Marked" ||
                    !selectedToStage
                  }
                >
                  {promotingStudentId === record.id ? (
                    <>
                      <i className="ti ti-loader animate-spin me-2" />
                      Promoting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-arrow-up me-2" />
                      Promote
                    </>
                  )}
                </button>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {isEdit ? "Edit" : "Student"} Promotion
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "Student"} Promotion
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Student Promotion</h4>
            </div>
            <div className="card-body">
              {/* Filters Section */}
              <div className="row gx-2 mb-4">
                <div className="col-md-3 mb-2">
                  <label className="form-label">Session</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedSession}
                    onChange={handleSessionChange}
                    required
                    disabled={isLoadingSessions}
                  >
                    <option value="">Select Session</option>
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-2">
                  <label className="form-label">Course</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedCourse}
                    onChange={(e) => handleCourseChange(e, courses)}
                    required
                    disabled={isLoadingCourses || !selectedSession}
                  >
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-2">
                  <label className="form-label">From Level</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedFromStage}
                    onChange={handleFromStageChange}
                    required
                    disabled={!selectedCourse}
                  >
                    <option value="">Select From Level</option>
                    {getStageOptions().map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-2">
                  <label className="form-label">To Level</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedToStage}
                    onChange={handleToStageChange}
                    disabled={true} // Disable the dropdown since we auto-select the next stage
                    required
                  >
                    <option value="">Select To Level</option>
                    {getToStageOptions().map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                  {selectedFromStage && !selectedToStage && isLastStage() ? (
                    <div className="text-warning mt-1 small">
                      <i className="ti ti-alert-triangle me-1"></i>
                      This is the last level in this course
                    </div>
                  ) : selectedFromStage && !selectedToStage ? (
                    <div className="text-danger mt-1 small">
                      No next level available
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Bulk Promotion Section - Only show when all filters including To Stage are selected */}
              {areAllFiltersWithToStageSelected() ? (
                <div className="card bg-light-info border-0 mb-4">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                      <div>
                        <h5 className="mb-1">Bulk Promotion</h5>
                        <p className="mb-0 text-muted">
                          Select multiple students to promote at once (
                          {selectedStudentKeys.length} selected)
                        </p>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handlePromoteAll}
                          disabled={
                            selectedStudentKeys.length === 0 || isPromotingBulk
                          }
                        >
                          {isPromotingBulk ? (
                            <span className="spinner-border spinner-border-sm me-2" />
                          ) : (
                            <i className="ti ti-users me-1"></i>
                          )}
                          {isPromotingBulk
                            ? "Promoting..."
                            : "Promote Selected"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Table Section */}
              {areAllFiltersWithToStageSelected() ? (
                isLoadingStudents ? (
                  <div className="text-center">Loading student data...</div>
                ) : (
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={transformedStudents}
                      rowKey="id"
                      Selection={true}
                      onSelectionChange={handleSelectionChange}
                      selectedKeys={selectedStudentKeys}
                      onSearchChange={handleSearchChange}
                      getCheckboxProps={getCheckboxProps}
                      pagination={{
                        current: pageNumber,
                        pageSize: recordsPerPage,
                        total: paginationData?.totalItems || 0,
                        onChange: handlePageChange,
                        pageSizeOptions: [10, 20, 30],
                      }}
                    />
                  </div>
                )
              ) : selectedFromStage && isLastStage() ? (
                <div className="text-center py-5">
                  <i className="ti ti-award fs-1 text-warning mb-3"></i>
                  <h4 className="text-warning">
                    Students are at the final level
                  </h4>
                  <p className="text-warning">
                    The selected level is the last level in this course.
                    Students cannot be promoted further.
                  </p>
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="ti ti-filter fs-1 text-muted mb-3"></i>
                  <h4>Please select all filters to view the list</h4>
                  <p className="text-warning">
                    {getMissingFiltersMessage()
                      ? `Missing filters: ${getMissingFiltersMessage()}`
                      : "Select all filters to see the student list"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Promotion Modals */}
      <PromotionModals
        studentToPromote={studentToPromote}
        toStageName={
          selectedToStage
            ? courseStages.find(
              (stage) => stage.id.toString() === selectedToStage
            )?.name
            : undefined
        }
        fromStageName={
          selectedFromStage
            ? courseStages.find(
              (stage) => stage.id.toString() === selectedFromStage
            )?.name
            : undefined
        }
        selectedCount={selectedStudentKeys.length}
        isPromoting={isPromoting}
        isPromotingBulk={isPromotingBulk}
        onPromote={confirmIndividualPromotion}
        onPromoteBulk={confirmBulkPromotion}
        showPromoteModal={showPromoteModal}
        setShowPromoteModal={setShowPromoteModal}
        showBulkPromoteModal={showBulkPromoteModal}
        setShowBulkPromoteModal={setShowBulkPromoteModal}
      />
    </>
  );
};

export default StudentPromotion;
